/**
 * OperationalChecklist — Passos de execução (protagonista da bancada).
 */
import React, { useMemo } from 'react';
import { Check } from 'lucide-react';

interface ChecklistItem {
  id: string;
  label: string;
  required?: boolean;
}

interface OperationalChecklistProps {
  items: ChecklistItem[];
  checklistState: Record<string, boolean>;
  onToggle: (itemId: string) => void;
  readOnly?: boolean;
}

export const OperationalChecklist: React.FC<OperationalChecklistProps> = ({
  items,
  checklistState,
  onToggle,
  readOnly,
}) => {
  const stats = useMemo(() => {
    const total = items.length;
    const done = items.filter((i) => checklistState[i.id]).length;
    return { total, done, percent: total > 0 ? Math.round((done / total) * 100) : 100 };
  }, [items, checklistState]);

  const currentIndex = useMemo(
    () => items.findIndex((item) => !checklistState[item.id]),
    [items, checklistState],
  );

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col">
      <ol className="relative space-y-0">
        {items.map((item, index) => {
          const isDone = checklistState[item.id] ?? false;
          const isCurrent = !readOnly && index === currentIndex;
          const isMissing = item.required && !isDone;
          const isLast = index === items.length - 1;

          return (
            <li key={item.id} className="relative flex gap-0">
              <div className="flex w-10 shrink-0 flex-col items-center pt-3">
                <span
                  className={`z-10 flex h-7 w-7 items-center justify-center rounded-full font-mono text-[11px] font-bold transition ${
                    isDone
                      ? 'bg-emerald-600 text-white'
                      : isCurrent
                        ? 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900/40'
                        : isMissing
                          ? 'border-2 border-amber-400 bg-white text-amber-700 dark:bg-slate-900'
                          : 'border-2 border-slate-200 bg-white text-slate-400 dark:border-slate-600 dark:bg-slate-900'
                  }`}
                >
                  {isDone ? <Check size={14} strokeWidth={3} /> : index + 1}
                </span>
                {!isLast && (
                  <div
                    className={`w-0.5 flex-1 min-h-[12px] ${isDone ? 'bg-emerald-300 dark:bg-emerald-800' : 'bg-slate-200 dark:bg-slate-700'}`}
                  />
                )}
              </div>

              <button
                type="button"
                onClick={() => !readOnly && onToggle(item.id)}
                disabled={readOnly}
                className={`mb-2 mt-2 flex min-w-0 flex-1 flex-col rounded-lg border px-4 py-3 text-left transition ${
                  isCurrent
                    ? 'border-blue-200 bg-blue-50/80 shadow-sm dark:border-blue-800/50 dark:bg-blue-950/25'
                    : isDone
                      ? 'border-transparent bg-transparent opacity-60'
                      : isMissing
                        ? 'border-amber-200/80 bg-amber-50/40 dark:border-amber-900/30 dark:bg-amber-950/10'
                        : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/30 dark:hover:border-slate-700'
                } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <span
                  className={`text-[13px] font-semibold leading-snug ${
                    isDone ? 'text-slate-400 line-through' : 'text-slate-900 dark:text-slate-50'
                  } ${isCurrent ? 'text-[14px]' : ''}`}
                >
                  {item.label}
                </span>
                {isCurrent && (
                  <span className="mt-1 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                    Passo atual — marque ao concluir
                  </span>
                )}
                {isMissing && !isCurrent && (
                  <span className="mt-0.5 text-[9px] font-bold uppercase text-amber-600">Obrigatório</span>
                )}
              </button>
            </li>
          );
        })}
      </ol>

      <p className="mt-1 pl-10 text-[9px] text-slate-400">
        {stats.done} de {stats.total} passos concluídos ({stats.percent}%)
      </p>
    </div>
  );
};

export default OperationalChecklist;
